import {
    LowLightDetect,
    Zoom,
    ViewPort,
    SSD,
    Cleanup,
    SlimZoom,
    SlimDrawNvCanvas,
    MotionGL,
    CreateImageBitmap,
    Cond,
    Connector,
} from './pipeline_jobs'
import * as tfvis from '@tensorflow/tfjs-vis'
import * as tf from '@tensorflow/tfjs'

const READ_STREAM = 'read'

function sort_by_order_and_prio(job_data_a, job_data_b) {
    const job_a = job_data_a.job
    const job_b = job_data_b.job
    let Pa = job_a.pirority
    let Pb = job_b.pirority
    const data_a = job_data_a.data
    const data_b = job_data_b.data
    let Ia = data_a.frame_id
    let Ib = data_b.frame_id
    // assume max pirority value is 100
    // return in decend order
    // return  Ia - Ib
    return Ia * 100 + Pb - (Ib * 100 + Pa)
}

function reloadWindow() {
    console.warn('Get context lost!!!')
    window.location.reload()
}

function reloadOnContextLost(canvas) {
    const type = 'webglcontextlost'
    canvas.removeEventListener(type, reloadWindow)
    canvas.addEventListener(type, reloadWindow)
}

export class Pipeline {
    constructor(
        video,
        videoCanvas,
        overlayCanvas,
        worker,
        outputCallback,
        fps = 15
    ) {
        this.log(`constructor - worker ${worker}`)
        this._video_source = video

        this._use_worker = worker !== undefined
        this._output_callback = this._wrap_warmup_callback(outputCallback)
        this._output_callback = this._wrap_job_trace_callback(
            this._output_callback
        )
        this.only_output_final = true
        if (worker) {
            this.worker_setup(worker, video, videoCanvas, overlayCanvas)
        } else {
            this.videoCanvas = videoCanvas
            this.videoCanvasCtx = videoCanvas.getContext('2d')
            this.overlayCanvas = overlayCanvas
            this.overlayCanvasCtx = overlayCanvas.getContext('2d')

            reloadOnContextLost(videoCanvas)
            reloadOnContextLost(overlayCanvas)

            tf.ready().then(() => {
                if (tf.getBackend() == 'webgl') {
                    reloadOnContextLost(tf.backend().canvas)
                    // setTimeout(()=>{
                    //     console.error('opps context lost!')
                    //     tf.backend().getGPGPUContext().gl.getExtension('WEBGL_lose_context').loseContext();
                    // }, 20000)
                }
            })
        }

        this._job_stack = []
        this._job_list = []
        this._first_job = null
        this._external_input_stack = []
        this._frame_count = 0
        this.fps = fps

        this._job_interval = 5
        this._num_exec = 0 // dont touch this
        this._max_jobs = 8 // but you can touch this
        this._max_exec = 3 // and this
        // if (!this._use_worker) {
        this.build_jobs()
        // }

        this.jobInterval = -1
        this.warmup_steps = 0
        this.warmup_target = 8
        this.warmup_per_job = {}
        for (const jn of this._job_list.map((v) => v.name))
            this.warmup_per_job[jn] = 0
        this.on_warmup_finish = undefined
        this.on_warmup_update = undefined

        this._build_time = new Date()
        this._build_time_str = `${this._build_time.getHours()}-${this._build_time.getMinutes()}-${this._build_time.getSeconds()}-${this._build_time.getMilliseconds()}`
        this._job_trace = {}
        this._job_trace_max_sample = 60
        this._input_event_history = {}

        if (typeof WorkerGlobalScope == 'undefined') {
            if (window.alfredTest2wsxsw2) {
                this.surface_pipe = tfvis
                    .visor()
                    .surface({ name: this.type(), tab: 'Pipeline' })
                this.runtime_interval = setInterval(
                    this.plot_jobs_runtime.bind(this),
                    1000
                )
                this.surface_trace = tfvis
                    .visor()
                    .surface({ name: this.type(), tab: 'JobTrace' })
                this.jobtrace_interval = setInterval(
                    this.plot_jobs_trace.bind(this),
                    1000
                )

                this.surface_param = tfvis
                    .visor()
                    .surface({ name: this.type(), tab: 'Parameter' })
                this.showparam_interval = setInterval(
                    this.plot_pipe_param.bind(this),
                    1000
                )

                // NOTE: scatter plot will slow down pipeline, not that useful.
                this.surface_jobs = {}
                // this.scatter_interval = setInterval(
                //     this.plot_jobs_scatter.bind(this),
                //     2000
                // )
                this.plot_jobs_runtime()
                this.log('tfvis - ', this.surface_pipe)
            }
        }
    }

    jobs_runtime() {
        const values = []
        for (const job of this._job_list) {
            const stat = job.runtime_statisic()
            values.push([
                job.name,
                stat.mean,
                stat.variance,
                stat.count,
                stat.fps,
            ])
        }
        return values
    }

    per_run_job_stat() {
        let value_dict = {}
        for (const job of this._job_list) {
            const first_run = Math.min(...job._run_timestamp)
            value_dict[job.name] = []
            for (let i = 0; i < job._run_timestamp.length; i++) {
                const stamp = job._run_timestamp[i]
                const cost = job._runtime_history[i]
                value_dict[job.name].push({
                    y: cost,
                    x: (stamp - first_run) / 1000,
                })
            }
        }
        return value_dict
    }

    jobs_trace_summary() {
        let elapse_mean_var_per_job = {}
        for (const [job_name, elapse_list] of Object.entries(this._job_trace)) {
            let in_sum = 0
            let out_sum = 0
            const n = elapse_list.length ? elapse_list.length : 1

            for (const { elapsed_in, elapsed_out } of elapse_list) {
                in_sum += elapsed_in
                out_sum += elapsed_out
            }
            const in_mean = in_sum / n
            const out_mean = out_sum / n

            let in_var = 0
            let out_var = 0
            for (const { elapsed_in, elapsed_out } of elapse_list) {
                in_var += Math.pow(elapsed_in - in_mean, 2)
                out_var += Math.pow(elapsed_out - out_mean, 2)
            }
            in_var /= n
            out_var /= n

            elapse_mean_var_per_job[job_name] = {
                enter_mean: in_mean,
                enter_var: in_var,
                esc_mean: out_mean,
                esc_var: out_var,
            }
        }
        return elapse_mean_var_per_job
    }

    plot_jobs_trace() {
        const mean_var_per_job = this.jobs_trace_summary()
        const headers = [
            'Job',
            'EnterTime mean',
            'EnterTime var',
            'ExitTime mean',
            'ExitTime var',
        ]
        let rows = []
        for (const [job_name, mean_var] of Object.entries(mean_var_per_job)) {
            rows.push([
                job_name,
                mean_var.enter_mean,
                mean_var.enter_var,
                mean_var.esc_mean,
                mean_var.esc_var,
            ])
        }
        if (this.surface_trace) {
            tfvis.render.table(this.surface_trace, {
                headers,
                values: rows,
            })
        }
    }

    plot_jobs_runtime(values = undefined) {
        if (this.surface_pipe) {
            const headers = ['Job', 'Mean', 'Var', 'Samples', 'FPS']

            if (values === undefined) {
                if (this._use_worker) {
                    this._worker.postMessage({
                        jobs_runtime: true,
                    })
                    return
                } else {
                    values = this.jobs_runtime()
                }
            }

            tfvis.render.table(this.surface_pipe, {
                headers,
                values,
            })
        }
    }

    param_summary() {
        let values = [
            ['videoCanvas_h', this.videoCanvas.height],
            ['videoCanvas_w', this.videoCanvas.width],
            ['frame_count', this._frame_count],
            ['warmup_steps', this.warmup_steps],
            ['warmup_target', this.warmup_target],
            ['fps', this.fps],
        ]

        if (this._video_source !== null && this._video_source !== undefined) {
            values.push(['video_source_w', this._video_source.width])
            values.push(['video_source_h', this._video_source.height])
        } else {
            if (this._video_source_meta !== undefined) {
                values.push(['video_source_w', this._video_source_meta.width])
                values.push(['video_source_h', this._video_source_meta.height])
            }
        }

        for (const key in this._input_event_history) {
            values.push([key, this._input_event_history[key]])
        }
        return values
    }

    plot_pipe_param(values = undefined) {
        if (this.surface_pipe) {
            const headers = ['Name', 'Value']

            if (values === undefined) {
                if (this._use_worker) {
                    this._worker.postMessage({
                        show_pipe_param: true,
                    })
                    return
                } else {
                    values = this.param_summary()
                }
            }

            tfvis.render.table(this.surface_param, {
                headers,
                values,
            })
        }
    }

    plot_jobs_scatter(values = undefined) {
        if (this.surface_pipe) {
            const series = ['run']

            if (values === undefined) {
                if (this._use_worker) {
                    this._worker.postMessage({
                        jobs_scatter: true,
                    })
                    return
                } else {
                    values = this.per_run_job_stat()
                }
            }

            for (const [name, value] of Object.entries(values)) {
                if (value.length > 1) {
                    // console.log('scatter surface: ', name, this.surface_jobs[name])
                    if (this.surface_jobs[name] == undefined) {
                        this.surface_jobs[name] = tfvis.visor().surface({
                            name: `${this.type()}:${name}`,
                            tab: 'Jobs',
                        })
                    }

                    const data = { values: [value], series }
                    tfvis.render.scatterplot(this.surface_jobs[name], data)
                }
            }
        }
    }

    log(...args) {
        console.debug(
            `[${this.constructor.name} ${this._build_time_str}] `,
            ...args
        )
    }

    type() {
        return 'Pipeline'
    }

    worker_setup(worker, video, videoCanvas, overlayCanvas) {
        this._worker = worker
        this._worker.addEventListener('message', (event) => {
            if (event.data.outputResult) {
                this._output_callback(event.data.outputResult)
            } else if (event.data.ready) {
                this.warmup()
            } else if (event.data.jobs_runtime) {
                this.plot_jobs_runtime(event.data.jobs_runtime)
            } else if (event.data.jobs_scatter) {
                this.plot_jobs_scatter(event.data.jobs_scatter)
            } else if (event.data.show_pipe_param) {
                this.plot_pipe_param(event.data.show_pipe_param)
            } else if (event.data.webglcontextlost === true) {
                window.location.reload()
            } else {
                this.log(`Pipeline worker output: `, event)
            }
        })
        this._worker.postMessage(
            {
                init_args: [null, videoCanvas, overlayCanvas, undefined],
                pipeline_type: this.type(),
            },
            [videoCanvas, overlayCanvas]
        )
    }

    set_worker_debug_canvas(debug_canvas) {
        this._worker.postMessage(
            {
                debug_canvas: debug_canvas,
            },
            [debug_canvas]
        )
    }

    build_jobs() {
        throw Error('Not implemented yet!')
    }

    set_frame_reader_stream(stream) {
        if (stream instanceof ReadableStream) {
            this._frame_reader_stream = stream
            this._frame_reader = stream.getReader()
        } else {
            throw Error(`Invalid type of frame_reader_stream: ${typeof stream}`)
        }
    }

    start_extract_frame() {
        if (this._pulling_loop != undefined)
            throw Error('Pipeline is already pulling frame from video_source!')
        const pulling_loop = () => {
            createImageBitmap(this._video_source)
                .then((bitmap) => {
                    if (this._use_worker)
                        this._worker.postMessage(
                            {
                                frame: bitmap,
                            },
                            [bitmap]
                        )
                    else this.send_input_frame(bitmap)
                })
                .catch((error) => {
                    console.error('start_extract_frame: ', error)
                })
        }
        this._pulling_loop = setInterval(pulling_loop, 1000 / this.fps)
        this.log(`start_extract_frame at ${this.fps} FPS`)
        // setTimeout(() => clearInterval(this._pulling_loop), 20000)

        if (this.jobtrace_interval === undefined) {
            this.jobtrace_interval = setInterval(
                this.plot_jobs_trace.bind(this),
                1000
            )
        }
        if (this.runtime_interval === undefined) {
            this.runtime_interval = setInterval(
                this.plot_jobs_runtime.bind(this),
                1000
            )
        }
        if (this.showparam_interval === undefined) {
            this.showparam_interval = setInterval(
                this.plot_pipe_param.bind(this),
                1000
            )
        }
    }

    stop_frame_extract() {
        if (this._pulling_loop) clearInterval(this._pulling_loop)
        if (this.jobInterval) clearTimeout(this.jobInterval)
        if (this.runtime_interval) clearTimeout(this.runtime_interval)
        if (this.jobtrace_interval) clearTimeout(this.jobtrace_interval)
        if (this.showparam_interval) clearTimeout(this.showparam_interval)
        this.runtime_interval = undefined
        this.jobtrace_interval = undefined
        this.showparam_interval = undefined
        this._pulling_loop = undefined
        if (this._frame_reader_stream) {
            if (!this._frame_reader_stream.locked) {
                // readable stream on the main thread will be locked
                let unhandle_promise = this._frame_reader_stream.cancel()
            } else {
                // tell worker to release stream from the main thread
                this._worker.postMessage({ cancel_reader_stream: true })
            }
        }
    }

    stop_worker() {
        if (this._use_worker) {
            this._worker.terminate()
        }
    }

    async warmup() {
        const num_branch = this._job_list.filter((job) => job instanceof Cond)
            .length
        const num_steps = Math.pow(2, num_branch) * this.warmup_target
        for (const k in this.warmup_per_job) this.warmup_per_job[k] = 0
        // NOTE: each forward pass will only go through one of the two path when encounter a switch,
        // so we need to double the warmup steps to be able to recive the targeted number of reuslts in the end of the pipeline
        if (this._use_worker) {
            if (this.warmup_steps < num_steps) {
                this._worker.postMessage({
                    call_warmup: true,
                })
            }
        } else {
            const empty_img = new ImageData(
                this.videoCanvas.width,
                this.videoCanvas.height
            )
            const bitmap = await createImageBitmap(empty_img)

            for (let i = this.warmup_steps; i < num_steps; i++) {
                // second run will let outputCallback know the progress of warmup with num_warmup
                this.exec_job(this._first_job, {
                    frame: bitmap,
                    frame_id: this._frame_count,
                    overlayCanvas: this.overlayCanvas,
                    overlayCanvasCtx: this.overlayCanvasCtx,
                    videoCanvas: this.videoCanvas,
                    videoCanvasCtx: this.videoCanvasCtx,
                    num_warmup: [],
                    force_run: true,
                })
            }
            this._frame_count++
        }
    }

    _wrap_warmup_callback(outputCallback) {
        const wrapper = (outputResult) => {
            if (outputResult.num_warmup) {
                const pipeline_warmup =
                    typeof outputResult.num_warmup == 'number'
                        ? outputResult.num_warmup
                        : outputResult.num_warmup.length
                for (const job_name of outputResult.num_warmup)
                    this.warmup_per_job[job_name]++
                const min_steps = Object.values(
                    this.warmup_per_job
                ).reduce((a, b) => (a > b ? b : a))
                this.log(
                    `this.warmup_steps: ${this.warmup_steps}`,
                    outputResult
                )
                // if (pipeline_warmup >= this._job_list.length) {
                this.warmup_steps = min_steps
                if (this.on_warmup_update) {
                    this.on_warmup_update(this.warmup_steps)
                }
                if (this.warmup_steps >= this.warmup_target) {
                    if (this.on_warmup_finish) {
                        this.on_warmup_finish()
                    }
                }
                // }
            }
            outputCallback(outputResult)
        }
        return wrapper
    }

    _wrap_job_trace_callback(outputCallback) {
        const wrapper = (outputResult) => {
            if (outputResult.frame_timestamp) {
                const trace = outputResult.frame_timestamp.trace_jobs
                for (const { elapsed_in, elapsed_out, job } of trace) {
                    if (this._job_trace[job] === undefined)
                        this._job_trace[job] = []
                    this._job_trace[job].push({ elapsed_in, elapsed_out })
                    if (
                        this._job_trace[job].length > this._job_trace_max_sample
                    )
                        this._job_trace[job].shift()
                }
            }
            outputCallback(outputResult)
        }
        return wrapper
    }

    extract_result(data) {
        let ele2rm = []
        let transferable = {}
        for (const key in data) {
            if (data[key] === undefined) continue

            if (
                data[key].constructor.name ==
                'OffscreenCanvasRenderingContext2D'
            ) {
                ele2rm.push(key)
            } else if (key == 'frame') {
                if (data[key] instanceof ImageBitmap) {
                    ele2rm.push(key)
                } else if (data[key] instanceof Array) {
                    if (data[key][0] instanceof ImageBitmap) {
                        ele2rm.push(key)
                    }
                }
            } else if (
                key == 'zoomarea_bitmap' &&
                data[key] instanceof ImageBitmap
            )
                ele2rm.push(key)
            else transferable[key] = data[key]
        }
        return transferable
    }

    exec_job(job, data) {
        this._num_exec += 1
        this.jobInterval = setTimeout(() => {
            job.run(data).then((result) => {
                if (result === undefined) {
                    // Do nothing for now
                } else {
                    let compact = this.extract_result(result)
                    if (this.only_output_final) {
                        const nomore = (j) => {
                            const cond_a = j.next === undefined
                            if (!cond_a) return j.next.length == 0
                            return true
                        }

                        let is_final = nomore(job)
                        if (
                            job.next.length === 1 &&
                            job.next[0] instanceof Cleanup
                        ) {
                            is_final = is_final || nomore(job.next[0])
                        }

                        if (is_final) this._output_callback(compact)
                    } else {
                        this._output_callback(compact)
                    }
                }

                if (job.next !== undefined && result !== undefined) {
                    if ('select_next_jobs' in result) {
                        const select_next_jobs = result.select_next_jobs
                        delete result.select_next_jobs
                        if (select_next_jobs instanceof Array) {
                            for (const select_next_job of select_next_jobs) {
                                this._job_stack.push({
                                    job: select_next_job,
                                    data: result,
                                })
                            }
                        } else {
                            this._job_stack.push({
                                job: select_next_jobs,
                                data: result,
                            })
                        }
                    } else {
                        for (const next of job.next) {
                            this._job_stack.push({
                                job: next,
                                data: result,
                            })
                        }
                    }
                    // NOTE: use some smart schdule algrothm here
                    this._job_stack = this._job_stack.sort(
                        sort_by_order_and_prio
                    )
                }
                this._num_exec -= 1

                this.jobInterval = setTimeout(() => {
                    if (this._job_stack.length > 0) {
                        const next_task = this._job_stack.shift()
                        this.exec_job(next_task.job, next_task.data)
                    }
                }, this._job_interval)
                // console.debug(this._job_stack)
            })
        }, this._job_interval)
    }

    send_input_frame(image_bitmap, kwargs = {}, taskId) {
        if (kwargs.frame !== undefined) delete kwargs.frame
        if (this._use_worker) {
            this._worker.postMessage({
                frame: image_bitmap,
                taskId,
                ...kwargs,
            })
        } else {
            let default_param_set = {
                frame: image_bitmap,
                frame_id: this._frame_count,
                frame_timestamp: {
                    process_pipe: this.type(),
                    timestamp: new Date().getTime(),
                    trace_jobs: [],
                },
                taskId,
                overlayCanvas: this.overlayCanvas,
                overlayCanvasCtx: this.overlayCanvasCtx,
                videoCanvas: this.videoCanvas,
                videoCanvasCtx: this.videoCanvasCtx,
                ...kwargs,
            }

            this._frame_count += 1

            if (this._debug_canvas !== undefined) {
                default_param_set['debug_canvas'] = this._debug_canvas
                default_param_set[
                    'debug_canvas_ctx'
                ] = this._debug_canvas.getContext('2d')
            }

            if (
                this._video_source === null ||
                this._video_source === undefined
            ) {
                // HACK: just for display pipeline param on tfvis, if this approch is causing problem in fature feel free to remove it.
                if (image_bitmap !== undefined) {
                    this._video_source_meta = {
                        width: image_bitmap.width,
                        height: image_bitmap.height,
                    }
                } else {
                    this._video_source_meta = {
                        width: this.videoCanvas.width,
                        height: this.videoCanvas.height,
                    }
                }
            }

            const external_param = this._external_input_stack.shift()
            const param = external_param
                ? { ...default_param_set, ...external_param }
                : default_param_set

            if (
                this._job_stack.length < this._max_jobs &&
                this._num_exec < this._max_exec
            ) {
                if (this._job_list.length > 0)
                    this.exec_job(this._first_job, param)
                else
                    this._job_stack.push({
                        job: this._first_job,
                        data: param,
                    })
            } else {
                // NOTE: insert extneral singal back to stack to make sure execution
                this._external_input_stack.unshift(external_param)
                // console.debug(`Job stack: ${this._job_stack.length}, ext stack: ${this._external_input_stack.length}, Num exec: ${this._num_exec}`)
            }
        }
    }

    send_input_event(arg_obj) {
        if (this._use_worker) {
            this._worker.postMessage({ input_event: arg_obj })
        } else {
            this.log('send_input_event: ', arg_obj)
            for (let key in arg_obj) {
                this._input_event_history[key] = arg_obj[key]
            }
            this._external_input_stack.push(arg_obj)
        }
    }
}

export class SlimPipe extends Pipeline {
    /*
        Pipeline similar to VideoStreamPipeline but doesn't have motion detection.
        SlimPipe apply every effect on video with canvas, hence not support web worker which
        recive image from main thread in ImageBitmap format.
        This pipeline is target for provide live capblity for low-end device, and serve as 
        a performance baseline during development.
    */
    type() {
        return 'SlimPipe'
    }

    start_extract_frame() {
        if (this._pulling_loop != undefined)
            throw Error('Pipeline is already pulling frame from video_source!')
        const pulling_loop = () => {
            this.videoCanvasCtx.drawImage(
                this._video_source,
                0,
                0,
                this.videoCanvas.width,
                this.videoCanvas.height
            )
            this.send_input_frame(undefined)
        }
        this._pulling_loop = setInterval(pulling_loop, 1000 / this.fps)
        this.log(`start_extract_frame at ${this.fps} FPS`)

        if (this.jobtrace_interval === undefined) {
            this.jobtrace_interval = setInterval(
                this.plot_jobs_trace.bind(this),
                1000
            )
        }
        if (this.runtime_interval === undefined) {
            this.runtime_interval = setInterval(
                this.plot_jobs_runtime.bind(this),
                1000
            )
        }
    }

    async warmup() {
        // SlimPipe don't use tfjs model, no warmup is required
        throw Error('SilmPipe dont need this')
    }

    worker_setup(worker, video, videoCanvas, overlayCanvas) {
        throw Error('SlimPipe dont support worker mode!')
    }

    build_jobs() {
        let nv = new SlimDrawNvCanvas('night-vision', [], 5, this)
        let low_light_detect = new LowLightDetect('lwdetect', [nv], 10, this)
        this._first_job = new SlimZoom('zoommmm', [low_light_detect], 5, this)
        this._job_list = [nv, low_light_detect, this._first_job]
    }
}

export class DualWieldPipe extends Pipeline {
    /*
        Pipeline using component from SilmPipe to processing video frames and
        using tfjs jobs from Allfunc to detect objects.
        Two branch of jobs have its own input frames rate.
        Video processing branch have same frames rate as source video,
        Detection branch use a much lower frame rate(deafult is 4) to reduce the time
        "createImageBitmap" is called.
    */

    type() {
        return 'DualWieldPipe'
    }

    start_extract_frame(method = 'frame_reader') {
        if (
            method == 'frame_reader' &&
            window.MediaStreamTrackProcessor === undefined
        )
            method = 'img_cap'
        if (this._pulling_loop != undefined)
            throw Error('Pipeline is already pulling frame from video_source!')
        let pulling_loop = null
        if (this._use_worker) {
            this._video_stream = this._video_source.captureStream()
            this._video_track = this._video_stream.getVideoTracks()[0]

            switch (method) {
                case 'img_cap':
                    this._video_cap = new ImageCapture(this._video_track)
                    pulling_loop = () => {
                        this._video_cap.grabFrame().then(
                            (bitmap) => {
                                this._worker.postMessage(
                                    {
                                        frame: bitmap,
                                    },
                                    [bitmap]
                                )
                            },
                            (error) => {
                                console.warn('_video_cap.grabFrame: ', error)
                            }
                        )
                    }
                    break
                case 'bitmap':
                    pulling_loop = () => {
                        createImageBitmap(this._video_source).then((bitmap) => {
                            this._worker.postMessage(
                                {
                                    frame: bitmap,
                                },
                                [bitmap]
                            )
                        })
                    }
                    break
                case 'frame_reader':
                    let processor = new window.MediaStreamTrackProcessor(
                        this._video_track
                    )
                    this._frame_reader_stream = processor.readable
                    this._worker.postMessage(
                        {
                            frame_reader_stream: this._frame_reader_stream,
                        },
                        [this._frame_reader_stream]
                    )
                    pulling_loop = () => {
                        this._worker.postMessage({ frame: READ_STREAM })
                    }
                    break
            }
        } else {
            pulling_loop = () => {
                this.videoCanvasCtx.drawImage(
                    this._video_source,
                    0,
                    0,
                    this.videoCanvas.width,
                    this.videoCanvas.height
                )
                this.send_input_frame(undefined)
            }
        }
        this._pulling_loop = setInterval(pulling_loop, 1000 / this.fps)
        this.log(`start_extract_frame at ${this.fps} FPS`)

        if (this.jobtrace_interval === undefined) {
            this.jobtrace_interval = setInterval(
                this.plot_jobs_trace.bind(this),
                1000
            )
        }
        if (this.runtime_interval === undefined) {
            this.runtime_interval = setInterval(
                this.plot_jobs_runtime.bind(this),
                1000
            )
        }
    }

    send_input_frame(image_object, kwargs = {}, taskId) {
        if (!this._use_worker) {
            if (image_object instanceof ImageBitmap) {
                const need_resize =
                    this.videoCanvas.width != image_object.width ||
                    this.videoCanvas.height != image_object.height
                if (need_resize) {
                    this.videoCanvasCtx.drawImage(
                        image_object,
                        0,
                        0,
                        image_object.width,
                        image_object.height,
                        0,
                        0,
                        this.videoCanvas.width,
                        this.videoCanvas.height
                    )
                    super.send_input_frame(undefined, kwargs, taskId)
                } else {
                    super.send_input_frame(image_object, kwargs, taskId)
                }
            } else if (image_object == READ_STREAM) {
                if (this._frame_reader.state == 'closed') {
                    this.log('Reset closed frame redaer')
                    this._frame_reader = this._frame_reader_stream.getReader()
                }
                this._frame_reader.read().then(({ done, value }) => {
                    this.videoCanvasCtx.drawImage(
                        value,
                        0,
                        0,
                        this.videoCanvas.width,
                        this.videoCanvas.height
                    )
                    super.send_input_frame(undefined, kwargs, taskId)
                    value.close()
                })
            } else {
                super.send_input_frame(image_object, kwargs, taskId)
            }
        } else {
            super.send_input_frame(image_object, kwargs, taskId)
        }
    }

    async warmup() {
        const num_branch = this._job_list.filter((job) => job instanceof Cond)
            .length
        const num_steps = Math.pow(2, num_branch) * this.warmup_target
        // NOTE: each forward pass will only go through one of the two path when encounter a switch,
        // so we need to double the warmup steps to be able to recive the targeted number of reuslts in the end of the pipeline
        if (!this._use_worker) {
            for (let i = this.warmup_steps; i < num_steps; i++) {
                this.exec_job(this._first_job, {
                    frame: undefined,
                    frame_id: this._frame_count,
                    overlayCanvas: this.overlayCanvas,
                    overlayCanvasCtx: this.overlayCanvasCtx,
                    videoCanvas: this.videoCanvas,
                    videoCanvasCtx: this.videoCanvasCtx,
                    num_warmup: [],
                    force_run: true,
                })
                this._frame_count++
            }
        } else {
            if (this.warmup_steps < num_steps) {
                this._worker.postMessage({
                    call_warmup: true,
                })
            }
        }
    }

    build_jobs() {
        let clean_ssd = new Cleanup('clean_ssd_tensor', [], 5, this, false)
        let ssd = new SSD('ssd', [clean_ssd], 5, this)
        let clean_vp = new Cleanup('clean_vp_tensor', [ssd], 5, this, true)
        let viewport = new ViewPort('viewp', [clean_vp], 5, this)

        let skip_connect = new Connector(
            'connect_to_output',
            [clean_ssd],
            5,
            this,
            (data) => {
                // NOTE:
                // Put empty pd result when there is no motion
                // (pipeline will skip viewport and ssd, hence so PD result to output).
                data['detect_boxes'] = []
                return data
            },
            () => {
                let tensors = viewport.claim_tensors()
                tensors = tensors.concat(ssd.claim_tensors())
                return tensors
            }
        )
        let motion_switch = new Cond(
            'switch_when_no_motion',
            skip_connect,
            viewport,
            10,
            this,
            (data) => data.motion_area === undefined
        )

        let motion = new MotionGL('mo_gl', [motion_switch], 5, this)
        let create_bitmap = new CreateImageBitmap('bitmap', [motion], 5, this)

        // let gl_motion = new SlimMotionGL('gl-motion', [], this, 5)
        // let nv = new SlimDrawNvCanvas('night-vision', [], this, 15)
        this._first_job = new SlimZoom('zoommmm', [create_bitmap], this, 5)
        this._job_list = [
            this._first_job,
            motion,
            motion_switch,
            skip_connect,
            viewport,
            ssd,
        ]
    }
}

export class FreeDualWieldPipe extends DualWieldPipe {
    type() {
        return 'FreeDualWieldPipe'
    }

    build_jobs() {
        let clean_ssd = new Cleanup('clean_ssd_tensor', [], 5, this, false)

        let motion = new MotionGL('mo_gl', [clean_ssd], 5, this)
        let create_bitmap = new CreateImageBitmap('bitmap', [motion], 5, this)

        this._first_job = new SlimZoom('zoommmm', [create_bitmap], this, 5)
        this._job_list = [this._first_job, create_bitmap, motion]
    }
}

export class PersonPipe extends DualWieldPipe {
    /*
        
    */

    type() {
        return 'PersonPipe'
    }

    build_jobs() {
        let clean_ssd = new Cleanup('clean_ssd_tensor', [], 5, this, false)

        /* Perosn Branch */
        let ssd = new SSD('ssd', [clean_ssd], 5, this)
        let clean_vp = new Cleanup('clean_vp_tensor', [ssd], 5, this, true)
        let viewport = new ViewPort('viewp', [clean_vp], 5, this)

        /* Skip Detect */
        let skip_connect = new Connector(
            'connect_to_output',
            [clean_ssd],
            5,
            this,
            (data) => {
                // NOTE:
                // Put empty pd result to tell callback function there is no person detected when there is no motion.
                // (pipeline will skip viewport and ssd, hence no PD result to output if we don't do this).
                data['detect_boxes'] = []
                return data
            },
            () => {
                let tensors = viewport.claim_tensors()
                tensors = tensors.concat(ssd.claim_tensors())
                return tensors
            }
        )

        /* Motion Switch */
        let motion_switch = new Cond(
            'switch_when_no_motion',
            skip_connect,
            viewport,
            10,
            this,
            (data) => data.motion_area === undefined
        )

        let motion = new MotionGL('mo_gl', [motion_switch], 5, this)
        let low_light_detect = new LowLightDetect(
            'lwdetect',
            [motion],
            10,
            this
        )

        if (typeof WorkerGlobalScope == 'undefined') {
            // On UI Thread
            let create_bitmap = new CreateImageBitmap(
                'bitmap',
                [low_light_detect],
                5,
                this,
                250
            )

            // let nv = new SlimDrawNvCanvas('night-vision-draw', [], this, 15)
            this._first_job = new SlimZoom(
                'zoom_with_canvas',
                [create_bitmap],
                this,
                5
            )
            this._job_list = [
                ssd,
                viewport,
                skip_connect,
                motion_switch,
                motion,
                low_light_detect,
                create_bitmap,
                this._first_job,
            ]
        } else {
            this._first_job = new Zoom(
                'zoom_with_bitmap',
                [low_light_detect],
                5,
                this
            )
            this._job_list = [
                ssd,
                viewport,
                skip_connect,
                motion_switch,
                motion,
                low_light_detect,
                this._first_job,
            ]
        }
    }
}

export class Empty extends DualWieldPipe {
    type() {
        return 'Empty'
    }

    warmup() {
        this.warmup_steps = this.warmup_target
        this.on_warmup_update()
        this.on_warmup_finish()
    }

    build_jobs() {
        this._first_job = new Cleanup('clean_md_tensor', [], 5, this, true)
        this._job_stack = []
    }
}

export class DevSlimPipe extends SlimPipe {
    /*
     */
    type() {
        return 'DevSlimPipe'
    }

    // build_jobs() {
    //     let gl_motion = new MotionGL('gl-motion', [], this, 5)
    //     let cbitmap = new CreateImageBitmap(
    //         'bitmap',
    //         [gl_motion],
    //         10,
    //         this,
    //         100
    //     )
    //     // let nv = new SlimDrawNvCanvas('night-vision', [], 5, this)

    //     let low_light_detect = new LowLightDetect(
    //         'lwdetect',
    //         [cbitmap],
    //         10,
    //         this
    //     )
    //     this._first_job = new SlimZoom('zoommmm', [low_light_detect], 5, this)
    //     this._job_list = [low_light_detect, this._first_job, gl_motion]
    // }

    build_jobs() {
        let clean_ssd = new Cleanup('clean_ssd_tensor', [], 5, this, false)
        let ssd = new SSD('ssd', [clean_ssd], 5, this)
        let clean_vp = new Cleanup('clean_vp_tensor', [ssd], 5, this, true)
        let viewport = new ViewPort('viewp', [clean_vp], 5, this)

        let skip_connect = new Connector(
            'connect_to_output',
            [clean_ssd],
            5,
            this,
            (data) => {
                // NOTE:
                // Put empty pd result when there is no motion
                // (pipeline will skip viewport and ssd, hence so PD result to output).
                data['detect_boxes'] = []
                return data
            },
            () => {
                let tensors = viewport.claim_tensors()
                tensors = tensors.concat(ssd.claim_tensors())
                return tensors
            }
        )
        let motion_switch = new Cond(
            'switch_when_no_motion',
            skip_connect,
            viewport,
            10,
            this,
            (data) => data.motion_area === undefined
        )

        let motion = new MotionGL('mo_gl', [motion_switch], 5, this)
        let create_bitmap = new CreateImageBitmap('bitmap', [motion], 5, this)

        // let gl_motion = new SlimMotionGL('gl-motion', [], this, 5)
        // let nv = new SlimDrawNvCanvas('night-vision', [], this, 15)
        this._first_job = new SlimZoom('zoommmm', [create_bitmap], this, 5)
        this._job_list = [
            this._first_job,
            motion,
            motion_switch,
            skip_connect,
            viewport,
            ssd,
        ]
    }
}
